import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { X } from 'lucide-react';
import AnalyticsCharts from './AnalyticsCharts';
import AddUserDialog from './components/AddUserDialog';
import { trackPageView, trackAdminPanelAccess } from './utils/analytics';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper function to strip HTML tags AND remove <style>/<script>/<head> contents
// so the Email History "Message Preview" column shows real human text instead of
// CSS rules from the embedded <style> block.
const stripHtmlTags = (html) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  // Remove style/script/head/meta blocks - their textContent leaks raw CSS
  temp.querySelectorAll('style, script, head, meta, link, title').forEach((n) => n.remove());
  return (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
};

function AdminPanel({ authToken, onViewAsUser, initialTab = 'users', showOnly = null }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRestaurantsDialog, setShowRestaurantsDialog] = useState(false);
  const [showVisitsDialog, setShowVisitsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRestaurants, setUserRestaurants] = useState([]);
  const [userVisits, setUserVisits] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [sortColumn, setSortColumn] = useState('display_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [restaurantSortColumn, setRestaurantSortColumn] = useState('name');
  const [restaurantSortOrder, setRestaurantSortOrder] = useState('asc');
  const [showAllVisitsDialog, setShowAllVisitsDialog] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurantVisits, setRestaurantVisits] = useState([]);
  const [hideEmails, setHideEmails] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [welcomeSending, setWelcomeSending] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  // Email History: pagination + per-row viewer modal + column sort
  const [emailHistoryPage, setEmailHistoryPage] = useState(1);
  const EMAIL_HISTORY_PAGE_SIZE = 20;
  const [viewingEmail, setViewingEmail] = useState(null);
  // sortBy: 'date' (default newest-first), 'recipient', 'subject'
  // sortDir: 'asc' or 'desc'
  const [emailSortBy, setEmailSortBy] = useState('date');
  const [emailSortDir, setEmailSortDir] = useState('desc');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showIndividualEmailDialog, setShowIndividualEmailDialog] = useState(false);
  const [selectedEmailUser, setSelectedEmailUser] = useState(null);
  const [individualSubject, setIndividualSubject] = useState('');
  const [individualMessage, setIndividualMessage] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [communicationsLoading, setCommunicationsLoading] = useState(true);
  const [enableHtml, setEnableHtml] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [enableIndividualHtml, setEnableIndividualHtml] = useState(false);
  // Footer settings removed - now hardcoded
  const [randomPickDistance, setRandomPickDistance] = useState('');
  const [randomPickDays, setRandomPickDays] = useState('');
  const [showIndividualHtmlPreview, setShowIndividualHtmlPreview] = useState(false);
  const emailTextareaRef = useRef(null);
  const individualTextareaRef = useRef(null);
  
  // Automated communications settings
  const [autoCommsSettings, setAutoCommsSettings] = useState({
    newRestaurant: { enabled: false, include_group: false, subject: '🍽️ New Restaurant Added!', message: 'A new restaurant "{restaurant_name}" has been added to your list.' },
    ratingChanged: { enabled: false, include_group: false, autoCheckEnabled: false, checkIntervalDays: 7, subject: '📊 Rating Update', message: 'The rating for "{restaurant_name}" has changed from {old_rating} to {new_rating}.' },
    weeklyDigest: { enabled: false, include_group: false, subject: '📅 Your Weekly Dining Digest', message: 'Here is your weekly summary of dining activity.', dayOfWeek: 'Monday', timeOfDay: '09:00' },
    visitReminder: { enabled: false, include_group: false, daysSinceVisit: 30, subject: '⏰ Time to Visit Again!', message: 'You haven\'t visited "{restaurant_name}" in {days} days. Time for a return visit?' }
  });
  const [savingAutoComms, setSavingAutoComms] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  
  // Analytics state
  const [activeTab, setActiveTab] = useState(initialTab); // users, restaurants, communications, analytics
  const [analyticsData, setAnalyticsData] = useState({
    pageViews: [],
    events: [],
    topPages: [],
    topEvents: []
  });

  useEffect(() => {
    // Track Admin Panel access
    trackAdminPanelAccess();
    trackPageView('/admin/panel', 'Admin Panel');
    
    // Load random pick settings from localStorage
    const savedRandomPickDistance = localStorage.getItem('randomPickDistance') || '25';
    const savedRandomPickDays = localStorage.getItem('randomPickDays') || '30';
    setRandomPickDistance(savedRandomPickDistance);
    setRandomPickDays(savedRandomPickDays);
    
    // Initial data fetch based on which tab is shown
    const loadData = () => {
      if (showOnly === 'communications') {
        fetchEmailHistory();
        fetchUsers(); // Need users count for email dialog
        fetchAutoCommsSettings(); // Load automated communication settings
      } else if (showOnly !== 'analytics') {
        fetchUsers();
        fetchAllRestaurants();
        fetchEmailHistory();
      }
    };
    
    loadData();
    
    // Set up periodic refresh every 5 minutes to keep session alive
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing admin panel data...');
      loadData();
    }, 5 * 60 * 1000); // 5 minutes
    
    // More frequent refresh for communications tab to show queue progress (every 10 seconds)
    let queueRefreshInterval;
    if (showOnly === 'communications') {
      queueRefreshInterval = setInterval(() => {
        fetchEmailHistory();
      }, 10000); // 10 seconds
    }
    
    // Refresh data when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab became visible, refreshing data...');
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup on unmount
    return () => {
      clearInterval(refreshInterval);
      if (queueRefreshInterval) clearInterval(queueRefreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showOnly]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.status === 401) {
        // Token expired - could trigger re-login here
        console.log('⚠️ Auth token expired for users fetch');
        setError('Session expired. Please refresh the page.');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRestaurants = async () => {
    try {
      const response = await fetch(`${API_URL}/api/all-restaurants`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.status === 401) {
        console.log('⚠️ Auth token expired for restaurants fetch');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch restaurants');
      }

      const data = await response.json();
      setAllRestaurants(data);
    } catch (err) {
      console.error('Error fetching all restaurants:', err);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-history?limit=10000`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch email history');
      }

      const data = await response.json();
      setEmailHistory(data);
    } catch (err) {
      console.error('Error fetching email history:', err);
      setEmailHistory([]);
    } finally {
      setCommunicationsLoading(false);
    }
  };

  const fetchAutoCommsSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-communications-settings`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setAutoCommsSettings(prev => ({ ...prev, ...data.settings }));
          // Restore saved test email address
          if (data.settings.testEmailAddress) {
            setTestEmailAddress(data.settings.testEmailAddress);
          }
          if (data.settings.adminEmail) {
            setAdminEmail(data.settings.adminEmail);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching auto communications settings:', err);
    }
  };

  const [sendingTestEmail, setSendingTestEmail] = useState(null);
  
  const sendTestEmail = async (type) => {
    if (!testEmailAddress || !testEmailAddress.trim()) {
      alert('Please enter a test email address first.');
      return;
    }
    setSendingTestEmail(type);
    try {
      // Shorter timeout since we're now using a queue
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(`${API_URL}/api/admin/test-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          type, 
          settings: autoCommsSettings[type],
          testEmail: testEmailAddress || null
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      if (response.ok) {
        // Show queued message instead of sent
        alert(`✅ Email Queued!\n\n${data.message}\n\n📬 Check email history in a few seconds for delivery status.`);
        // Refresh email history after a short delay to show the sent email
        setTimeout(() => fetchEmailHistory(), 5000);
      } else {
        alert(`❌ Failed to queue email:\n${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      if (err.name === 'AbortError') {
        alert('⏱️ Request timed out. Please try again.');
      } else {
        alert('❌ Failed to send test email. Please try again.');
      }
    } finally {
      setSendingTestEmail(null);
    }
  };

  const saveAutoCommsSettings = async () => {
    setSavingAutoComms(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/auto-communications-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings: { ...autoCommsSettings, testEmailAddress, adminEmail } }),
      });
      if (response.ok) {
        alert('✅ Automated communication settings saved!');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving auto communications settings:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSavingAutoComms(false);
    }
  };

  const handleDeleteEmail = async (emailId, subject) => {
    if (!window.confirm(`Are you sure you want to delete this email?\n\nSubject: ${subject}`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/email-logs/${emailId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete email log');
      }

      // Refresh email history after deletion
      await fetchEmailHistory();
      
      // Show success message (optional)
      console.log('Email log deleted successfully');
    } catch (err) {
      console.error('Error deleting email:', err);
      alert('Failed to delete email log. Please try again.');
    }
  };

  const fetchUserRestaurants = async (userEmail) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(userEmail)}/restaurants`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user restaurants');
      }

      const data = await response.json();
      setUserRestaurants(data);
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      setUserRestaurants([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchUserVisits = async (userEmail) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(userEmail)}/visits`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user visits');
      }

      const data = await response.json();
      setUserVisits(data);
    } catch (err) {
      console.error('Error fetching visits:', err);
      setUserVisits([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRestaurantsClick = (user) => {
    if (user.total_restaurants === 0) return;
    setSelectedUser(user);
    setShowRestaurantsDialog(true);
    fetchUserRestaurants(user.email);
  };

  const handleVisitsClick = (user) => {
    if (user.total_visits === 0) return;
    setSelectedUser(user);
    setShowVisitsDialog(true);
    fetchUserVisits(user.email);
  };


  const handleBlockUser = async (user) => {
    const action = user.blocked ? 'unblock' : 'block';
    if (!window.confirm(`Are you sure you want to ${action} ${user.email}?`)) {
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(user.email)}/block`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      alert(response.data.message);
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error blocking/unblocking user:', error);
      alert('Failed to ' + action + ' user. Please try again.');
    }
  };


  const handleToggleEmailOptIn = async (user) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(user.email)}/email-opt-in`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error toggling email opt-in:', error);
      alert('Failed to update email settings. Please try again.');
    }
  };

  const handleToggleAllEmails = async () => {
    // Check if any users have emails enabled
    const anyEnabled = users.some(u => u.email_opt_in !== false);
    const newStatus = !anyEnabled; // If any enabled, turn all off; otherwise turn all on
    
    try {
      await axios.put(
        `${API_URL}/api/admin/users/email-opt-in/all`,
        { email_opt_in: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error toggling all emails:', error);
      alert('Failed to update email settings. Please try again.');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(
      `Are you sure you want to DELETE ${user.email}?\n\n` +
      `This will permanently delete:\n` +
      `- ${user.total_restaurants} restaurant(s)\n` +
      `- ${user.total_visits} visit(s)\n` +
      `- All their photos\n\n` +
      `This action CANNOT be undone!`
    )) {
      return;
    }

    // Double confirmation for delete
    if (!window.confirm(`FINAL CONFIRMATION: Delete ${user.email} and ALL their data?`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_URL}/api/admin/users/${encodeURIComponent(user.email)}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      alert(response.data.message);
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Failed to delete user. Please try again.');
    }
  };

  const handleChangeRole = async (user, makeAdmin) => {
    if (!!user.is_admin === makeAdmin) return;
    const verb = makeAdmin ? 'Promote' : 'Demote';
    if (!window.confirm(`${verb} ${user.display_name || user.email} to ${makeAdmin ? 'Admin' : 'User'}?`)) return;
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(user.email)}/role`,
        { is_admin: makeAdmin },
        { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' } }
      );
      setUsers((prev) => prev.map((u) => (u.email === user.email ? { ...u, is_admin: makeAdmin } : u)));
      alert(`✅ ${response.data.message}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to change role. Please try again.');
    }
  };

  const handleSendWelcome = async (user) => {
    const pinNote = user.pin_is_temporary
      ? 'A NEW temporary PIN will be generated and included in the email (any older PIN stops working).'
      : 'They already set their own PIN, so no PIN is included.';
    if (!window.confirm(`Send the welcome email to ${user.display_name || user.email}?\n\n${pinNote}`)) return;
    setWelcomeSending(user.email);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/users/${encodeURIComponent(user.email)}/send-welcome`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setUsers((prev) => prev.map((u) => (u.email === user.email ? { ...u, welcome_sent_at: response.data.welcome_sent_at } : u)));
      alert(`✅ ${response.data.message}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send welcome email. Please try again.');
    } finally {
      setWelcomeSending(null);
    }
  };

  const handleResetUserPin = async (user) => {
    const newPin = window.prompt(
      `Reset PIN for ${user.display_name || user.email}?\n\n` +
      `Enter new PIN (minimum 4 characters):`,
      ''
    );
    
    if (newPin === null) {
      return; // User cancelled
    }
    
    if (!newPin || newPin.length < 4) {
      alert('PIN must be at least 4 characters');
      return;
    }
    
    if (!window.confirm(`Set new PIN "${newPin}" for ${user.email}?`)) {
      return;
    }
    
    try {
      const response = await axios.put(
        `${API_URL}/api/admin/users/${encodeURIComponent(user.email)}/reset-pin`,
        { new_pin: newPin },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      alert(`✅ ${response.data.message}\n\nNew PIN: ${newPin}`);
    } catch (error) {
      console.error('Error resetting PIN:', error);
      alert(error.response?.data?.detail || 'Failed to reset PIN. Please try again.');
    }
  };

  const handleDeleteVisit = async (visit) => {
    if (!window.confirm(
      `Are you sure you want to DELETE this visit?\n\n` +
      `Restaurant: ${visit.restaurantName || 'Unknown'}\n` +
      `Date: ${visit.visitDate}\n` +
      `User: ${visit.userName || 'Unknown'}\n\n` +
      `This action CANNOT be undone!`
    )) {
      return;
    }

    try {
      setLoadingDetails(true); // Show loading state
      
      const response = await axios.delete(
        `${API_URL}/api/admin/visits/${visit.id}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        }
      );

      console.log('Visit deleted successfully:', response.data);
      
      // Refresh the visits list
      if (selectedUser) {
        await fetchUserVisits(selectedUser.email);
      }
      
      alert(response.data.message);
    } catch (error) {
      console.error('Error deleting visit:', error);
      alert(error.response?.data?.detail || 'Failed to delete visit. Please try again.');
      setLoadingDetails(false);
    }
  };




  const fetchRestaurantVisits = async (restaurantId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/api/visits/${restaurantId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch restaurant visits');
      }

      const data = await response.json();
      setRestaurantVisits(data);
    } catch (err) {
      console.error('Error fetching restaurant visits:', err);
      setRestaurantVisits([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRestaurantClick = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setShowAllVisitsDialog(true);
    fetchRestaurantVisits(restaurant.id);
  };

  const maskEmail = (email) => {
    if (!hideEmails || !email) return email;
    const [username, domain] = email.split('@');
    if (!domain) return email;
    
    // Show first 2 characters of username and first character of domain
    const maskedUsername = username.length > 2 
      ? username.substring(0, 2) + '***' 
      : '***';
    const maskedDomain = domain.charAt(0) + '***';
    
    return `${maskedUsername}@${maskedDomain}`;
  };


  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      alert('Please enter both subject and message');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: emailSubject,
          message: emailMessage,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }

      const result = await response.json();
      alert(`Email sent successfully to ${result.primary_recipient} with ${result.bcc_count} users in BCC!`);
      
      // Reset form
      setEmailSubject('');
      setEmailMessage('');
      setShowEmailDialog(false);
      
      // Refresh email history
      fetchEmailHistory();
    } catch (err) {
      alert(`Error sending email: ${err.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleOpenIndividualEmail = (user) => {
    setSelectedEmailUser(user);
    setIndividualSubject('');
    setIndividualMessage('');
    setShowIndividualEmailDialog(true);
  };

  const handleSendIndividualEmail = async () => {
    if (!individualSubject.trim() || !individualMessage.trim()) {
      alert('Please enter both subject and message');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/send-individual-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_email: selectedEmailUser.email,
          recipient_name: selectedEmailUser.display_name,
          subject: individualSubject,
          message: individualMessage,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }

      const result = await response.json();
      alert(`Email sent successfully to ${selectedEmailUser.display_name}!`);
      
      // Reset form
      setIndividualSubject('');
      setIndividualMessage('');
      setSelectedEmailUser(null);
      setShowIndividualEmailDialog(false);
      
      // Refresh email history
      fetchEmailHistory();
    } catch (err) {
      alert(`Error sending email: ${err.message}`);
    } finally {
      setSendingEmail(false);
    }
  };


  // HTML formatting functions
  const insertHtmlTag = (tag, isIndividual = false) => {
    const textarea = isIndividual ? individualTextareaRef.current : emailTextareaRef.current;
    const message = isIndividual ? individualMessage : emailMessage;
    const setMessage = isIndividual ? setIndividualMessage : setEmailMessage;
    
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    
    let newText = '';
    let cursorOffset = 0;
    
    switch(tag) {
      case 'bold':
        newText = `<strong>${selectedText || 'bold text'}</strong>`;
        cursorOffset = selectedText ? newText.length : 8;
        break;
      case 'italic':
        newText = `<em>${selectedText || 'italic text'}</em>`;
        cursorOffset = selectedText ? newText.length : 4;
        break;
      case 'heading':
        newText = `<h1>${selectedText || 'Heading'}</h1>`;
        cursorOffset = selectedText ? newText.length : 4;
        break;
      case 'link':
        const url = selectedText || 'https://';
        newText = `<a href="${url}">link text</a>`;
        cursorOffset = 9 + url.length;
        break;
      case 'list':
        newText = `<ul>\n  <li>${selectedText || 'List item'}</li>\n</ul>`;
        cursorOffset = selectedText ? newText.length : 10;
        break;
      case 'paragraph':
        newText = `<p>${selectedText || 'Paragraph text'}</p>`;
        cursorOffset = selectedText ? newText.length : 3;
        break;
      default:
        return;
    }
    
    const updatedMessage = message.substring(0, start) + newText + message.substring(end);
    setMessage(updatedMessage);
    
    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + cursorOffset, start + cursorOffset);
    }, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortedUsers = () => {
    const sorted = [...users].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Convert to lowercase for string comparison
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    return sorted;
  };

  const handleRestaurantSort = (column) => {
    if (restaurantSortColumn === column) {
      setRestaurantSortOrder(restaurantSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setRestaurantSortColumn(column);
      setRestaurantSortOrder('asc');
    }
  };

  const getSortedRestaurants = () => {
    const sorted = [...allRestaurants].sort((a, b) => {
      let aVal = a[restaurantSortColumn];
      let bVal = b[restaurantSortColumn];

      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Convert to lowercase for string comparison
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (restaurantSortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    return sorted;
  };

  const RestaurantSortIndicator = ({ column }) => {
    if (restaurantSortColumn === column) {
      return (
        <span className="sort-indicator active">
          {restaurantSortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      );
    }
    return (
      <span className="sort-indicator inactive">
        ⇅
      </span>
    );
  };


  const SortIndicator = ({ column }) => {
    if (sortColumn === column) {
      return (
        <span className="sort-indicator active">
          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      );
    }
    return (
      <span className="sort-indicator inactive">
        {' ⇅'}
      </span>
    );
  };

  const handleBackupDatabase = async () => {
    if (!window.confirm('Send database backup to support@dineasaurus.com?')) {
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await axios.post(
        `${API_URL}/api/admin/backup-database`,
        {},
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.status === 200) {
        const result = response.data;
        alert(`✅ Backup successful!\n\nFilename: ${result.backup_filename}\nTotal Records: ${result.total_records}\nSize: ${result.size_kb.toFixed(2)} KB\n\nEmail sent to: ${result.email_sent_to}`);
      }
    } catch (error) {
      console.error('Backup error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`❌ Backup failed.\n\nError: ${errorMsg}\n\nPlease try again or check with support.`);
    }
  };

  const handleRestoreDatabase = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Confirm before restore
    if (!window.confirm(`⚠️ WARNING: This will REPLACE all data in the Preview database with the backup file.\n\nFile: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\n\nAre you sure you want to continue?`)) {
      event.target.value = ''; // Clear file input
      return;
    }

    try {
      // Read file content
      const fileText = await file.text();
      const backupData = JSON.parse(fileText);

      // Validate backup format
      if (!backupData.collections) {
        throw new Error('Invalid backup file format - missing collections data');
      }

      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const response = await axios.post(
        `${API_URL}/api/admin/restore-database`,
        backupData,
        {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        const collectionsInfo = Object.entries(result.restored_collections)
          .map(([name, count]) => `- ${name}: ${count} records`)
          .join('\n');
        
        alert(`✅ Database restored successfully!\n\nBackup Date: ${result.backup_date}\nTotal Records: ${result.total_records}\n\nRestored Collections:\n${collectionsInfo}\n\n🔄 Please refresh the page to see updated data.`);
        
        // Refresh the page after restore
        window.location.reload();
      }
    } catch (error) {
      console.error('Restore error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`❌ Restore failed.\n\nError: ${errorMsg}\n\nPlease check the backup file format and try again.`);
    } finally {
      event.target.value = ''; // Clear file input
    }
  };

  // If showOnly is 'analytics', only render the analytics charts (skip loading/error states for users)
  if (showOnly === 'analytics') {
    return (
      <div className="admin-panel">
        <div className="admin-panel-header">
          <h2 className="admin-title">Dashboard Analytics</h2>
        </div>
        <AnalyticsCharts authToken={authToken} />
      </div>
    );
  }

  // If showOnly is 'communications', only render the communications panel
  if (showOnly === 'communications') {
    return (
      <div className="admin-panel">
        <div className="admin-panel-header">
          <h2 className="admin-title">Communications</h2>
          <div className="admin-header-actions">
            <button 
              className="send-email-btn"
              onClick={() => setShowEmailDialog(true)}
              title="Send email to all users"
            >
              📧 Send Email
            </button>
          </div>
        </div>
        
        {/* Test Email Address Field */}
        <div style={{ 
          marginBottom: '1.5rem', 
          padding: '1rem', 
          backgroundColor: '#fef3c7', 
          border: '1px solid #fcd34d', 
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#92400e' }}>
            📬 Test Email Address:
          </span>
          <input
            type="email"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            placeholder="Enter email to receive test notifications"
            style={{ 
              flex: 1, 
              minWidth: '250px',
              padding: '0.5rem 0.75rem', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px', 
              fontSize: '0.9rem' 
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.8rem', color: '#92400e' }}>
              {testEmailAddress ? `Tests will be sent to: ${testEmailAddress}` : 'Enter an email address to enable Send Test buttons'}
            </span>
            <span style={{ fontSize: '0.7rem', color: '#b45309' }}>
              ⏱️ Sending may take a few seconds
            </span>
          </div>
        </div>
        
        {/* Administrative Email Field */}
        <div style={{ 
          marginBottom: '1.5rem', 
          padding: '1rem', 
          backgroundColor: '#ede9fe', 
          border: '1px solid #c4b5fd', 
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: '600', fontSize: '0.9rem', color: '#5b21b6' }}>
            📧 Administrative Email:
          </span>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            placeholder="Enter email for access requests & admin alerts"
            data-testid="admin-email-input"
            style={{ 
              flex: 1, 
              minWidth: '250px',
              padding: '0.5rem 0.75rem', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px', 
              fontSize: '0.9rem' 
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.8rem', color: '#5b21b6' }}>
              {adminEmail ? `Access requests will be sent to: ${adminEmail}` : 'Enter an email to receive new access requests'}
            </span>
          </div>
        </div>
        
        {/* Automated Communications Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem' }}>
            🤖 Automated Notifications
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
            
            {/* New Restaurant Added */}
            <div style={{ 
              backgroundColor: autoCommsSettings.newRestaurant.enabled ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${autoCommsSettings.newRestaurant.enabled ? '#86efac' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>🍽️ New Restaurant Added</span>
                <button
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    newRestaurant: { ...prev.newRestaurant, enabled: !prev.newRestaurant.enabled }
                  }))}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                    backgroundColor: autoCommsSettings.newRestaurant.enabled ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative'
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.newRestaurant.enabled ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Notify user when they add a new restaurant
              </p>
              <input
                type="text"
                value={autoCommsSettings.newRestaurant.subject}
                onChange={(e) => setAutoCommsSettings(prev => ({
                  ...prev,
                  newRestaurant: { ...prev.newRestaurant, subject: e.target.value }
                }))}
                placeholder="Email subject"
                style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '0.5rem' }}
              />
              {/* Include Group toggle */}
              <label
                title="If on, this email also goes to every other user in your group(s)"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.78rem', color: '#475569',
                  cursor: 'pointer'
                }}
              >
                <span>👥 Include Group</span>
                <button
                  type="button"
                  data-testid="newrestaurant-include-group-toggle"
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    newRestaurant: { ...prev.newRestaurant, include_group: !prev.newRestaurant.include_group }
                  }))}
                  style={{
                    width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                    backgroundColor: autoCommsSettings.newRestaurant.include_group ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative', flexShrink: 0
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.newRestaurant.include_group ? '18px' : '2px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </label>
              <button
                onClick={() => sendTestEmail('newRestaurant')}
                disabled={sendingTestEmail === 'newRestaurant'}
                style={{
                  backgroundColor: '#3b82f6', color: 'white', padding: '0.35rem 0.75rem',
                  borderRadius: '4px', border: 'none', fontSize: '0.75rem', cursor: 'pointer',
                  opacity: sendingTestEmail === 'newRestaurant' ? 0.7 : 1
                }}
              >
                {sendingTestEmail === 'newRestaurant' ? '⏳ Queuing...' : '📤 Send Test'}
              </button>
            </div>

            {/* Rating Changed (Google) */}
            <div style={{ 
              backgroundColor: autoCommsSettings.ratingChanged.enabled ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${autoCommsSettings.ratingChanged.enabled ? '#86efac' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>Rating Changed</span>
                <button
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    ratingChanged: { ...prev.ratingChanged, enabled: !prev.ratingChanged.enabled }
                  }))}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                    backgroundColor: autoCommsSettings.ratingChanged.enabled ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative'
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.ratingChanged.enabled ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Notify when a restaurant's Google rating changes
              </p>
              
              {/* Unified Auto-check settings */}
              <div style={{ 
                backgroundColor: '#f8fafc', 
                border: '1px solid #e2e8f0', 
                borderRadius: '6px', 
                padding: '0.75rem', 
                marginBottom: '0.75rem' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Auto-check <span style={{color:'#4285f4'}}>Google</span> Ratings</span>
                  <button
                    onClick={() => setAutoCommsSettings(prev => ({
                      ...prev,
                      ratingChanged: { ...prev.ratingChanged, autoCheckEnabled: !prev.ratingChanged.autoCheckEnabled }
                    }))}
                    style={{
                      width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                      backgroundColor: autoCommsSettings.ratingChanged.autoCheckEnabled ? '#10b981' : '#cbd5e1',
                      cursor: 'pointer', position: 'relative'
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: '2px',
                      left: autoCommsSettings.ratingChanged.autoCheckEnabled ? '18px' : '2px',
                      width: '16px', height: '16px', borderRadius: '50%',
                      backgroundColor: 'white', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                    }} />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Check every</span>
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={autoCommsSettings.ratingChanged.checkIntervalDays || 7}
                    onChange={(e) => setAutoCommsSettings(prev => ({
                      ...prev,
                      ratingChanged: { ...prev.ratingChanged, checkIntervalDays: parseInt(e.target.value) || 7 }
                    }))}
                    disabled={!autoCommsSettings.ratingChanged.autoCheckEnabled}
                    style={{ 
                      width: '50px', 
                      padding: '0.25rem', 
                      fontSize: '0.75rem', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '4px',
                      textAlign: 'center',
                      opacity: autoCommsSettings.ratingChanged.autoCheckEnabled ? 1 : 0.5
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>days</span>
                </div>
                <p style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.4rem' }}>
                  Checks Google ratings. Set to 1 for nightly checks.
                </p>
              </div>
              
              <input
                type="text"
                value={autoCommsSettings.ratingChanged.subject}
                onChange={(e) => setAutoCommsSettings(prev => ({
                  ...prev,
                  ratingChanged: { ...prev.ratingChanged, subject: e.target.value }
                }))}
                placeholder="Email subject"
                style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '0.5rem' }}
              />
              {/* Include Group toggle */}
              <label
                title="If on, this email also goes to every other user in your group(s)"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.78rem', color: '#475569',
                  cursor: 'pointer'
                }}
              >
                <span>👥 Include Group</span>
                <button
                  type="button"
                  data-testid="ratingchanged-include-group-toggle"
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    ratingChanged: { ...prev.ratingChanged, include_group: !prev.ratingChanged.include_group }
                  }))}
                  style={{
                    width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                    backgroundColor: autoCommsSettings.ratingChanged.include_group ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative', flexShrink: 0
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.ratingChanged.include_group ? '18px' : '2px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </label>
              <button
                onClick={() => sendTestEmail('ratingChanged')}
                disabled={sendingTestEmail === 'ratingChanged'}
                style={{
                  backgroundColor: '#3b82f6', color: 'white', padding: '0.35rem 0.75rem',
                  borderRadius: '4px', border: 'none', fontSize: '0.75rem', cursor: 'pointer',
                  opacity: sendingTestEmail === 'ratingChanged' ? 0.7 : 1
                }}
              >
                {sendingTestEmail === 'ratingChanged' ? 'Queuing...' : 'Send Test'}
              </button>
            </div>

            {/* Weekly Digest */}
            <div style={{ 
              backgroundColor: autoCommsSettings.weeklyDigest.enabled ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${autoCommsSettings.weeklyDigest.enabled ? '#86efac' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>📅 Weekly Digest</span>
                <button
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    weeklyDigest: { ...prev.weeklyDigest, enabled: !prev.weeklyDigest.enabled }
                  }))}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                    backgroundColor: autoCommsSettings.weeklyDigest.enabled ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative'
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.weeklyDigest.enabled ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Send weekly summary of dining activity
              </p>
              
              {/* Day and Time Selectors */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.7rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Day</label>
                  <select
                    value={autoCommsSettings.weeklyDigest.dayOfWeek || 'Monday'}
                    onChange={(e) => setAutoCommsSettings(prev => ({
                      ...prev,
                      weeklyDigest: { ...prev.weeklyDigest, dayOfWeek: e.target.value }
                    }))}
                    style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                  >
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.7rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Time</label>
                  <select
                    value={autoCommsSettings.weeklyDigest.timeOfDay || '09:00'}
                    onChange={(e) => setAutoCommsSettings(prev => ({
                      ...prev,
                      weeklyDigest: { ...prev.weeklyDigest, timeOfDay: e.target.value }
                    }))}
                    style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                  >
                    <option value="06:00">6:00 AM</option>
                    <option value="07:00">7:00 AM</option>
                    <option value="08:00">8:00 AM</option>
                    <option value="09:00">9:00 AM</option>
                    <option value="10:00">10:00 AM</option>
                    <option value="11:00">11:00 AM</option>
                    <option value="12:00">12:00 PM</option>
                    <option value="13:00">1:00 PM</option>
                    <option value="14:00">2:00 PM</option>
                    <option value="15:00">3:00 PM</option>
                    <option value="16:00">4:00 PM</option>
                    <option value="17:00">5:00 PM</option>
                    <option value="18:00">6:00 PM</option>
                    <option value="19:00">7:00 PM</option>
                    <option value="20:00">8:00 PM</option>
                  </select>
                </div>
              </div>
              
              <input
                type="text"
                value={autoCommsSettings.weeklyDigest.subject}
                onChange={(e) => setAutoCommsSettings(prev => ({
                  ...prev,
                  weeklyDigest: { ...prev.weeklyDigest, subject: e.target.value }
                }))}
                placeholder="Email subject"
                style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '0.5rem' }}
              />
              <button
                onClick={() => sendTestEmail('weeklyDigest')}
                disabled={sendingTestEmail === 'weeklyDigest'}
                style={{
                  backgroundColor: '#3b82f6', color: 'white', padding: '0.35rem 0.75rem',
                  borderRadius: '4px', border: 'none', fontSize: '0.75rem', cursor: 'pointer',
                  opacity: sendingTestEmail === 'weeklyDigest' ? 0.7 : 1
                }}
              >
                {sendingTestEmail === 'weeklyDigest' ? '⏳ Queuing...' : '📤 Send Test'}
              </button>
            </div>

            {/* Visit Reminder */}
            <div style={{ 
              backgroundColor: autoCommsSettings.visitReminder.enabled ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${autoCommsSettings.visitReminder.enabled ? '#86efac' : '#e2e8f0'}`,
              borderRadius: '8px',
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>⏰ Visit Reminder</span>
                <button
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    visitReminder: { ...prev.visitReminder, enabled: !prev.visitReminder.enabled }
                  }))}
                  style={{
                    width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                    backgroundColor: autoCommsSettings.visitReminder.enabled ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative'
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.visitReminder.enabled ? '22px' : '2px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>
                Remind users about restaurants they haven't visited
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>After</span>
                <input
                  type="number"
                  value={autoCommsSettings.visitReminder.daysSinceVisit}
                  onChange={(e) => setAutoCommsSettings(prev => ({
                    ...prev,
                    visitReminder: { ...prev.visitReminder, daysSinceVisit: parseInt(e.target.value) || 30 }
                  }))}
                  min="1"
                  max="365"
                  style={{ width: '60px', padding: '0.3rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                />
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>days</span>
              </div>
              <input
                type="text"
                value={autoCommsSettings.visitReminder.subject}
                onChange={(e) => setAutoCommsSettings(prev => ({
                  ...prev,
                  visitReminder: { ...prev.visitReminder, subject: e.target.value }
                }))}
                placeholder="Email subject"
                style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '0.5rem' }}
              />
              {/* Include Group toggle */}
              <label
                title="If on, this email also goes to every other user in your group(s)"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.78rem', color: '#475569',
                  cursor: 'pointer'
                }}
              >
                <span>👥 Include Group</span>
                <button
                  type="button"
                  data-testid="visitreminder-include-group-toggle"
                  onClick={() => setAutoCommsSettings(prev => ({
                    ...prev,
                    visitReminder: { ...prev.visitReminder, include_group: !prev.visitReminder.include_group }
                  }))}
                  style={{
                    width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                    backgroundColor: autoCommsSettings.visitReminder.include_group ? '#10b981' : '#cbd5e1',
                    cursor: 'pointer', position: 'relative', flexShrink: 0
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: autoCommsSettings.visitReminder.include_group ? '18px' : '2px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s'
                  }} />
                </button>
              </label>
              <button
                onClick={() => sendTestEmail('visitReminder')}
                disabled={sendingTestEmail === 'visitReminder'}
                style={{
                  backgroundColor: '#3b82f6', color: 'white', padding: '0.35rem 0.75rem',
                  borderRadius: '4px', border: 'none', fontSize: '0.75rem', cursor: 'pointer',
                  opacity: sendingTestEmail === 'visitReminder' ? 0.7 : 1
                }}
              >
                {sendingTestEmail === 'visitReminder' ? '⏳ Queuing...' : '📤 Send Test'}
              </button>
            </div>
          </div>
          
          {/* Save Button */}
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={saveAutoCommsSettings}
              disabled={savingAutoComms}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                fontWeight: '600',
                cursor: savingAutoComms ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                opacity: savingAutoComms ? 0.7 : 1
              }}
            >
              {savingAutoComms ? 'Saving...' : '💾 Save Settings'}
            </button>
          </div>
        </div>

        {/* Email History Section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1e293b', margin: 0 }}>
            📬 Email History
          </h3>
          <button
            onClick={async () => {
              if (!window.confirm('⚠️ Delete ALL email history?\n\nThis will remove all sent, queued, and failed emails from the log.\n\nThis cannot be undone.')) {
                return;
              }
              try {
                const response = await fetch(`${API_URL}/api/admin/purge-email-history`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${authToken}` }
                });
                const data = await response.json();
                if (response.ok) {
                  alert(`✅ Purged ${data.deleted_logs} email logs and ${data.deleted_queue} queued emails.`);
                  fetchEmailHistory();
                } else {
                  alert(`❌ Error: ${data.detail || 'Failed to purge'}`);
                }
              } catch (err) {
                console.error('Purge error:', err);
                alert('❌ Failed to purge email history');
              }
            }}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            🗑️ Purge All
          </button>
        </div>
        <div className="communications-table-wrapper">
          {communicationsLoading ? (
            <div className="loading-state">Loading communications...</div>
          ) : emailHistory.length === 0 ? (
            <div className="empty-state">
              <p>No emails sent yet</p>
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Date & Time</th>
                  <th
                    data-testid="email-history-sort-recipient"
                    onClick={() => {
                      if (emailSortBy === 'recipient') {
                        setEmailSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      } else {
                        setEmailSortBy('recipient');
                        setEmailSortDir('asc');
                      }
                      setEmailHistoryPage(1);
                    }}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by recipient"
                  >
                    Recipients{' '}
                    <span style={{ fontSize: '0.8em', opacity: emailSortBy === 'recipient' ? 1 : 0.4 }}>
                      {emailSortBy === 'recipient' ? (emailSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th
                    data-testid="email-history-sort-subject"
                    onClick={() => {
                      if (emailSortBy === 'subject') {
                        setEmailSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      } else {
                        setEmailSortBy('subject');
                        setEmailSortDir('asc');
                      }
                      setEmailHistoryPage(1);
                    }}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Click to sort by subject"
                  >
                    Subject{' '}
                    <span style={{ fontSize: '0.8em', opacity: emailSortBy === 'subject' ? 1 : 0.4 }}>
                      {emailSortBy === 'subject' ? (emailSortDir === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th>Message Preview</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Build a sorted copy according to the active column.
                  // Default ('date'+'desc') preserves the server's newest-first order.
                  const getRecipient = (e) =>
                    e.email_type === 'mass'
                      ? `__ZZZ_mass_${e.recipients?.total || 0}` // group mass at end when asc
                      : (e.recipients?.recipient_name || e.recipients?.recipient_email || '').toLowerCase();
                  const getSubject = (e) => (e.subject || '').toLowerCase();
                  const getDate = (e) => new Date(e.sent_at || 0).getTime();

                  const sorted = [...emailHistory];
                  if (emailSortBy === 'recipient') {
                    sorted.sort((a, b) => getRecipient(a).localeCompare(getRecipient(b)));
                    if (emailSortDir === 'desc') sorted.reverse();
                  } else if (emailSortBy === 'subject') {
                    sorted.sort((a, b) => getSubject(a).localeCompare(getSubject(b)));
                    if (emailSortDir === 'desc') sorted.reverse();
                  } else {
                    sorted.sort((a, b) => getDate(b) - getDate(a));
                    if (emailSortDir === 'asc') sorted.reverse();
                  }

                  return sorted
                    .slice((emailHistoryPage - 1) * EMAIL_HISTORY_PAGE_SIZE, emailHistoryPage * EMAIL_HISTORY_PAGE_SIZE)
                    .map((email) => {
                  // Determine row styling based on queue_status
                  const status = email.queue_status || 'sent';
                  const rowStyle = {
                    queued: { backgroundColor: '#fef3c7', borderLeft: '4px solid #f59e0b' },
                    processing: { backgroundColor: '#dbeafe', borderLeft: '4px solid #3b82f6' },
                    failed: { backgroundColor: '#fee2e2', borderLeft: '4px solid #ef4444' },
                    permanently_failed: { backgroundColor: '#fecaca', borderLeft: '4px solid #b91c1c' },
                    sent: { backgroundColor: '#dcfce7', borderLeft: '4px solid #22c55e' }
                  }[status] || {};
                  
                  const statusBadge = {
                    queued: { label: '⏳ Queued', color: '#92400e', bg: '#fef3c7' },
                    processing: { label: '🔄 Sending...', color: '#1e40af', bg: '#dbeafe' },
                    failed: { label: `❌ Failed (${email.attempts || 1}/3)`, color: '#b91c1c', bg: '#fee2e2' },
                    permanently_failed: { label: '💀 Failed', color: '#7f1d1d', bg: '#fecaca' },
                    sent: { label: '✅ Sent', color: '#166534', bg: '#dcfce7' }
                  }[status] || { label: '✅ Sent', color: '#166534', bg: '#dcfce7' };
                  
                  return (
                    <tr key={email.id} style={rowStyle}>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: statusBadge.bg,
                          color: statusBadge.color
                        }}>
                          {statusBadge.label}
                        </span>
                        {email.last_error && (
                          <div style={{ fontSize: '0.65rem', color: '#b91c1c', marginTop: '2px' }}>
                            {email.last_error.substring(0, 40)}...
                          </div>
                        )}
                      </td>
                      <td className="date-cell">
                        <div className="date-time-wrapper">
                          <span className="date-main">{new Date(email.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="time-sub" style={{ marginLeft: '6px' }}>{new Date(email.sent_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                        </div>
                      </td>
                      <td className="recipients-cell">
                        <button
                          type="button"
                          data-testid={`view-email-${email.id}-btn`}
                          onClick={() => setViewingEmail(email)}
                          title="Click to view the email that was sent"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#2563eb',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            font: 'inherit'
                          }}
                        >
                          {email.email_type === 'mass' ? (
                            <span>👥 {email.recipients.total} users</span>
                          ) : (
                            <span>👤 {email.recipients?.recipient_name || maskEmail(email.recipients?.recipient_email)}</span>
                          )}
                        </button>
                      </td>
                      <td className="subject-cell" title={email.subject}>{email.subject}</td>
                      <td className="preview-cell" title={stripHtmlTags(email.message)}>
                        {stripHtmlTags(email.message).substring(0, 50)}...
                      </td>
                      <td className="delete-cell">
                        {status === 'sent' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            {email.email_type !== 'mass' && (
                              <button
                                data-testid={`resend-email-${email.id}-btn`}
                                onClick={async () => {
                                  const toAddr = email.recipients?.recipient_email || '';
                                  if (!window.confirm(`Resend "${email.subject}" to ${toAddr}?`)) return;
                                  try {
                                    const resp = await fetch(`${API_URL}/api/admin/email-logs/${email.id}/resend`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    const data = await resp.json().catch(() => ({}));
                                    if (!resp.ok) {
                                      alert(`❌ ${data.detail || 'Resend failed'}`);
                                      return;
                                    }
                                    alert(`📤 Re-queued for ${toAddr}`);
                                    setTimeout(() => fetchEmailHistory(), 2500);
                                  } catch (err) {
                                    alert(`❌ Resend error: ${err.message}`);
                                  }
                                }}
                                title="Re-send this exact email to the same recipient"
                                style={{
                                  backgroundColor: '#2563eb',
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                📤 Resend
                              </button>
                            )}
                            <button
                              className="delete-email-btn"
                              onClick={() => handleDeleteEmail(email.id, email.subject)}
                              title="Delete this email log"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        ) : status === 'failed' || status === 'permanently_failed' ? (
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${API_URL}/api/admin/process-email-queue`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${authToken}` }
                                });
                                alert('🔄 Retrying failed emails...');
                                setTimeout(() => fetchEmailHistory(), 3000);
                              } catch (err) {
                                console.error('Retry error:', err);
                              }
                            }}
                            style={{
                              backgroundColor: '#f59e0b',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: 'none',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 Retry
                          </button>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Processing...</span>
                        )}
                      </td>
                    </tr>
                  );
                });
                })()}
              </tbody>
            </table>
          )}

          {/* Pagination controls */}
          {emailHistory.length > EMAIL_HISTORY_PAGE_SIZE && (
            <div
              className="email-history-pagination"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '12px',
                padding: '8px'
              }}
            >
              <button
                type="button"
                data-testid="email-history-prev-btn"
                onClick={() => setEmailHistoryPage((p) => Math.max(1, p - 1))}
                disabled={emailHistoryPage === 1}
                style={{
                  padding: '6px 14px',
                  fontSize: '1rem',
                  background: emailHistoryPage === 1 ? '#e5e7eb' : '#2563eb',
                  color: emailHistoryPage === 1 ? '#9ca3af' : '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: emailHistoryPage === 1 ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
                title="Previous page"
              >
                ◀ Prev
              </button>
              <span style={{ fontSize: '0.9rem', color: '#475569' }}>
                Page <strong>{emailHistoryPage}</strong> of{' '}
                <strong>{Math.max(1, Math.ceil(emailHistory.length / EMAIL_HISTORY_PAGE_SIZE))}</strong>
                {' '}({emailHistory.length} total)
              </span>
              <button
                type="button"
                data-testid="email-history-next-btn"
                onClick={() =>
                  setEmailHistoryPage((p) =>
                    Math.min(Math.ceil(emailHistory.length / EMAIL_HISTORY_PAGE_SIZE), p + 1)
                  )
                }
                disabled={emailHistoryPage >= Math.ceil(emailHistory.length / EMAIL_HISTORY_PAGE_SIZE)}
                style={{
                  padding: '6px 14px',
                  fontSize: '1rem',
                  background:
                    emailHistoryPage >= Math.ceil(emailHistory.length / EMAIL_HISTORY_PAGE_SIZE)
                      ? '#e5e7eb'
                      : '#2563eb',
                  color:
                    emailHistoryPage >= Math.ceil(emailHistory.length / EMAIL_HISTORY_PAGE_SIZE)
                      ? '#9ca3af'
                      : '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor:
                    emailHistoryPage >= Math.ceil(emailHistory.length / EMAIL_HISTORY_PAGE_SIZE)
                      ? 'not-allowed'
                      : 'pointer',
                  fontWeight: 600
                }}
                title="Next page"
              >
                Next ▶
              </button>
            </div>
          )}

          {/* Stats Summary Line */}
          <div className="admin-stats-summary">
            <span className="stat-summary-item">
              <strong>Total:</strong> {emailHistory.length}
            </span>
            <span className="stat-summary-separator">|</span>
            <span className="stat-summary-item" style={{ color: '#22c55e' }}>
              <strong>Sent:</strong> {emailHistory.filter(e => e.queue_status === 'sent' || !e.queue_status).length}
            </span>
            <span className="stat-summary-separator">|</span>
            <span className="stat-summary-item" style={{ color: '#f59e0b' }}>
              <strong>Pending:</strong> {emailHistory.filter(e => e.queue_status === 'queued' || e.queue_status === 'processing').length}
            </span>
            <span className="stat-summary-separator">|</span>
            <span className="stat-summary-item" style={{ color: '#ef4444' }}>
              <strong>Failed:</strong> {emailHistory.filter(e => e.queue_status === 'failed' || e.queue_status === 'permanently_failed').length}
            </span>
          </div>
        </div>
        
        {/* Email Viewer Modal — opened by clicking a recipient name */}
        <Dialog open={!!viewingEmail} onOpenChange={(open) => !open && setViewingEmail(null)}>
          <DialogContent
            className="email-viewer-dialog"
            style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}
          >
            <DialogHeader>
              <DialogTitle>
                📧 {viewingEmail?.subject || 'Email'}
              </DialogTitle>
            </DialogHeader>
            {viewingEmail && (
              <div className="email-viewer-body" style={{ paddingTop: '8px' }}>
                <div
                  style={{
                    fontSize: '0.85rem',
                    color: '#475569',
                    marginBottom: '12px',
                    padding: '8px 12px',
                    background: '#f1f5f9',
                    borderRadius: '6px',
                    lineHeight: 1.6
                  }}
                >
                  <div>
                    <strong>To:</strong>{' '}
                    {viewingEmail.email_type === 'mass'
                      ? `👥 ${viewingEmail.recipients?.total || 0} users`
                      : `${viewingEmail.recipients?.recipient_name || ''} <${viewingEmail.recipients?.recipient_email || ''}>`}
                  </div>
                  <div>
                    <strong>From:</strong> {viewingEmail.sender_email || 'support@dineasaurus.com'}
                  </div>
                  <div>
                    <strong>Sent:</strong>{' '}
                    {viewingEmail.sent_at
                      ? new Date(viewingEmail.sent_at).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                        })
                      : '—'}
                  </div>
                  <div>
                    <strong>Status:</strong>{' '}
                    {viewingEmail.queue_status || (viewingEmail.actually_sent ? 'sent' : 'unknown')}
                  </div>
                </div>
                {/* Render the actual email HTML in a sandboxed iframe so its CSS
                    doesn't leak into the admin panel. */}
                <iframe
                  data-testid="email-viewer-iframe"
                  title="email-body"
                  srcDoc={viewingEmail.message || '<p>(no body)</p>'}
                  sandbox=""
                  style={{
                    width: '100%',
                    minHeight: '60vh',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    background: '#fff'
                  }}
                />
                <div style={{ textAlign: 'right', marginTop: '12px' }}>
                  <button
                    type="button"
                    data-testid="email-viewer-close-btn"
                    onClick={() => setViewingEmail(null)}
                    style={{
                      padding: '8px 16px',
                      background: '#64748b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="email-dialog">
            <DialogHeader>
              <DialogTitle>📧 Send Email to All Users</DialogTitle>
            </DialogHeader>
            <div className="email-form">
              <label>Subject:</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject..."
              />
              <label>Message:</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Enter your message..."
                rows={8}
              />
              <p className="email-note">
                This will send an email to all {users.filter(u => u.email_opt_in !== false).length} users with emails enabled.
              </p>
              <div className="email-actions">
                <button onClick={() => setShowEmailDialog(false)} className="cancel-btn">
                  Cancel
                </button>
                <button onClick={handleSendEmail} className="send-btn">
                  Send Email
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="admin-error" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ marginBottom: '1rem' }}>Error: {error}</p>
          <button 
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchUsers();
              fetchAllRestaurants();
              fetchEmailHistory();
            }}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Admin Panel Header with Controls - Only shown for 'users' or no filter */}
      {(!showOnly || showOnly === 'users') && (
        <div className="admin-panel-header">
          <h2 className="admin-title">User Management</h2>
          <div className="admin-header-actions">
            <button
              className="hide-emails-btn"
              onClick={() => setShowAddUser(true)}
              title="Add a new user"
              data-testid="add-user-btn"
            >
              ➕ Add User
            </button>
            <button 
              className="hide-emails-btn"
              onClick={() => setHideEmails(!hideEmails)}
              title={hideEmails ? "Show email addresses" : "Hide email addresses"}
            >
              {hideEmails ? '👁️ Show Emails' : '🙈 Hide Emails'}
            </button>
          </div>
        </div>
      )}
      <AddUserDialog open={showAddUser} onClose={() => setShowAddUser(false)} authToken={authToken} onCreated={() => fetchUsers()} />

      {/* Analytics Charts - Only shown when no filter (legacy) */}
      {!showOnly && <AnalyticsCharts authToken={authToken} />}

      {/* Settings Panels - Only shown when showOnly is 'users' or no filter */}
      {(!showOnly || showOnly === 'users') && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '2rem',
          marginBottom: '1rem' 
        }}>
          
          {/* Random Pick Settings - Compact Inline */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            flexWrap: 'wrap',
            padding: '0.75rem 1rem',
            backgroundColor: '#f8fafc', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px',
            width: '100%'
          }}>
            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              🎲 Random Pick:
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Distance</label>
              <input
                type="number"
                value={randomPickDistance}
                onChange={(e) => setRandomPickDistance(e.target.value)}
                placeholder="25"
                min="1"
                max="500"
                style={{ 
                  width: '70px', 
                  padding: '0.4rem 0.5rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}
              />
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>mi</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.85rem', color: '#64748b' }}>Not visited in</label>
              <input
                type="number"
                value={randomPickDays}
                onChange={(e) => setRandomPickDays(e.target.value)}
                placeholder="30"
                min="1"
                max="365"
                style={{ 
                  width: '70px', 
                  padding: '0.4rem 0.5rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}
              />
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>days</span>
            </div>
            <button
              onClick={() => {
                localStorage.setItem('randomPickDistance', randomPickDistance);
                localStorage.setItem('randomPickDays', randomPickDays);
                alert('✅ Random Pick settings saved!');
              }}
              style={{ 
                backgroundColor: '#10b981',
                color: 'white',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: 'none',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Users Table - Only shown when showOnly is 'users' or no filter */}
      {(!showOnly || showOnly === 'users') && (
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('display_name')}>
                  Display Name<SortIndicator column="display_name" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('email')}>
                  Email<SortIndicator column="email" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('is_admin')}>
                  Role<SortIndicator column="is_admin" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('total_restaurants')}>
                  Restaurants<SortIndicator column="total_restaurants" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('total_visits')}>
                  Visits<SortIndicator column="total_visits" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('last_activity')}>
                  Last Activity<SortIndicator column="last_activity" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('created_at')}>
                  Account Created<SortIndicator column="created_at" />
                </th>
                <th className="sortable-header" onClick={() => handleSort('blocked')}>
                  Status<SortIndicator column="blocked" />
                </th>
                <th 
                  className="sortable-header" 
                  onClick={handleToggleAllEmails}
                  title="Click to toggle all users' email settings"
                  style={{ cursor: 'pointer' }}
                >
                  Emails {users.every(u => u.email_opt_in !== false) ? '✓' : users.some(u => u.email_opt_in !== false) ? '◐' : '○'}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getSortedUsers().map((user) => (
                <tr key={user.email} className={user.blocked ? 'blocked-user-row' : ''}>
                  <td className="user-display-name">
                    <button
                      className="view-as-user-link"
                      onClick={() => onViewAsUser && onViewAsUser(user)}
                      title="View website as this user"
                    >
                      {user.display_name}
                    </button>
                  </td>
                  <td className="user-email">
                    <button
                      className="email-link-btn"
                      onClick={() => handleOpenIndividualEmail(user)}
                      title={`Send email to ${user.display_name}`}
                  >
                    {maskEmail(user.email)}
                  </button>
                </td>
                <td>
                  <select
                    className={`role-badge ${user.is_admin ? 'admin-badge' : 'user-badge'}`}
                    value={user.is_admin ? 'admin' : 'user'}
                    onChange={(e) => handleChangeRole(user, e.target.value === 'admin')}
                    title="Change role"
                    style={{ cursor: 'pointer', border: 'none', appearance: 'auto' }}
                    data-testid={`role-select-${user.email}`}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td 
                  className={`number-cell ${user.total_restaurants > 0 ? 'clickable' : ''}`}
                  onClick={() => handleRestaurantsClick(user)}
                  style={{ cursor: user.total_restaurants > 0 ? 'pointer' : 'default' }}
                >
                  {user.total_restaurants}
                </td>
                <td 
                  className={`number-cell ${user.total_visits > 0 ? 'clickable' : ''}`}
                  onClick={() => handleVisitsClick(user)}
                  style={{ cursor: user.total_visits > 0 ? 'pointer' : 'default' }}
                >
                  {user.total_visits}
                </td>
                <td className="date-cell">{formatDate(user.last_activity)}</td>
                <td className="date-cell">{formatDate(user.created_at)}</td>
                <td>
                  {user.blocked ? (
                    <span className="status-badge blocked-badge">🚫 Blocked</span>
                  ) : (
                    <span className="status-badge active-badge">✓ Active</span>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => handleToggleEmailOptIn(user)}
                    title={user.email_opt_in !== false ? 'Click to disable emails' : 'Click to enable emails'}
                    style={{
                      width: '40px',
                      height: '22px',
                      borderRadius: '11px',
                      border: 'none',
                      backgroundColor: user.email_opt_in !== false ? '#10b981' : '#cbd5e1',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '2px',
                      left: user.email_opt_in !== false ? '20px' : '2px',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left 0.2s'
                    }} />
                  </button>
                </td>
                <td className="action-buttons-cell">
                  <button
                    className="action-btn welcome-btn"
                    onClick={() => handleSendWelcome(user)}
                    disabled={welcomeSending === user.email}
                    title={user.welcome_sent_at ? `Welcome sent ${formatDate(user.welcome_sent_at)} — click to re-send` : 'Send welcome email (includes a temporary PIN for new accounts)'}
                    style={{ backgroundColor: user.welcome_sent_at ? '#0ea5e9' : '#f59e0b', color: 'white' }}
                    data-testid={`welcome-btn-${user.email}`}
                  >
                    {welcomeSending === user.email ? '⏳ Sending…' : user.welcome_sent_at ? '✉️ Re-send' : '✉️ Welcome'}
                  </button>
                  {!user.is_admin && (
                    <>
                      <button
                        className="action-btn reset-pin-btn"
                        onClick={() => handleResetUserPin(user)}
                        title="Reset user PIN/password"
                        style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                      >
                        🔑 Reset PIN
                      </button>
                      <button
                        className={`action-btn ${user.blocked ? 'unblock-btn' : 'block-btn'}`}
                        onClick={() => handleBlockUser(user)}
                        title={user.blocked ? 'Unblock user' : 'Block user'}
                      >
                        {user.blocked ? '🔓 Unblock' : '🔒 Block'}
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete user and all their data"
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                  {user.is_admin && (
                    <span className="admin-protected-text">Protected</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Stats Summary Line */}
        <div className="admin-stats-summary">
          <span className="stat-summary-item">
            <strong>Total Users:</strong> {users.length}
          </span>
          <span className="stat-summary-separator">|</span>
          <span className="stat-summary-item">
            <strong>Total Restaurants:</strong> {users.reduce((sum, user) => sum + user.total_restaurants, 0)}
          </span>
          <span className="stat-summary-separator">|</span>
          <span className="stat-summary-item">
            <strong>Total Visits:</strong> {users.reduce((sum, user) => sum + user.total_visits, 0)}
          </span>
        </div>
      </div>
      )}

      {/* Restaurants Dialog */}
      <Dialog open={showRestaurantsDialog} onOpenChange={setShowRestaurantsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.display_name}'s Restaurants ({userRestaurants.length})
            </DialogTitle>
            <button
              onClick={() => setShowRestaurantsDialog(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X size={20} />
            </button>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="loading-message">Loading restaurants...</div>
          ) : userRestaurants.length === 0 ? (
            <div className="empty-message">No restaurants found</div>
          ) : (
            <div className="dialog-table-wrapper">
              <table className="dialog-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Cuisine</th>
                    <th>Price</th>
                    <th>Rating</th>
                    <th>Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {userRestaurants.map((restaurant) => (
                    <tr key={restaurant.id}>
                      <td className="restaurant-name">{restaurant.name}</td>
                      <td>{restaurant.location}</td>
                      <td>{restaurant.ethnicity || '–'}</td>
                      <td>{restaurant.price || '–'}</td>
                      <td>
                        {restaurant.overallRating 
                          ? `${restaurant.overallRating.toFixed(1)} / 10` 
                          : '–'}
                      </td>
                      <td className="text-center">{restaurant.visitCount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Visits Dialog */}
      <Dialog open={showVisitsDialog} onOpenChange={setShowVisitsDialog}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.display_name}'s Visits ({userVisits.length})
            </DialogTitle>
            <button
              onClick={() => setShowVisitsDialog(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X size={20} />
            </button>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="loading-message">Loading visits...</div>
          ) : userVisits.length === 0 ? (
            <div className="empty-message">No visits found</div>
          ) : (
            <div className="dialog-table-wrapper">
              <table className="dialog-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Restaurant</th>
                    <th>Location</th>
                    <th>Rating</th>
                    <th>Diner</th>
                    <th>Guests</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userVisits.map((visit, index) => (
                    <tr key={`${visit.id}-${index}`}>
                      <td className="visit-date">{visit.visitDate}</td>
                      <td className="restaurant-name">{visit.restaurantName || 'Unknown'}</td>
                      <td>{visit.restaurantLocation || 'Unknown'}</td>
                      <td>
                        {visit.rating 
                          ? `${visit.rating} / 10` 
                          : '–'}
                      </td>
                      <td>{visit.userName || '–'}</td>
                      <td>{visit.guests || '–'}</td>
                      <td className="notes-cell">
                        {visit.notes ? (
                          <div className="notes-preview" title={visit.notes}>
                            {visit.notes.substring(0, 50)}
                            {visit.notes.length > 50 ? '...' : ''}
                          </div>
                        ) : '–'}
                      </td>
                      <td className="action-buttons-cell">
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteVisit(visit)}
                          title="Delete this visit"
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* All Visits for Restaurant Dialog */}
      <Dialog open={showAllVisitsDialog} onOpenChange={setShowAllVisitsDialog}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              All Visits to {selectedRestaurant?.name} ({restaurantVisits.length})
            </DialogTitle>
            <button
              onClick={() => setShowAllVisitsDialog(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X size={20} />
            </button>
          </DialogHeader>

          {loadingDetails ? (
            <p className="loading-text">Loading visits...</p>
          ) : restaurantVisits.length === 0 ? (
            <p className="no-data-text">No visits recorded yet.</p>
          ) : (
            <div className="dialog-table-wrapper">
              <table className="dialog-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Rating</th>
                    <th>Diner</th>
                    <th>Guests</th>
                    <th>Favorite Items</th>
                    <th>Dislikes</th>
                    <th>Notes</th>
                    <th>Staff Notes</th>
                    <th>Bathroom Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {restaurantVisits.map((visit, index) => (
                    <tr key={index}>
                      <td>{visit.visitDate}</td>
                      <td>
                        {visit.rating ? (
                          <span className="rating-display">⭐ {visit.rating}/10</span>
                        ) : '–'}
                      </td>
                      <td>{visit.userName || '–'}</td>
                      <td>{visit.guests || '–'}</td>
                      <td>{visit.favoriteItems || '–'}</td>
                      <td>{visit.dislikes || '–'}</td>
                      <td className="notes-cell">
                        {visit.notes ? (
                          <div className="notes-preview" title={visit.notes}>
                            {visit.notes.substring(0, 50)}
                            {visit.notes.length > 50 ? '...' : ''}
                          </div>
                        ) : '–'}
                      </td>
                      <td className="notes-cell">
                        {visit.staffNotes ? (
                          <div className="notes-preview" title={visit.staffNotes}>
                            {visit.staffNotes.substring(0, 50)}
                            {visit.staffNotes.length > 50 ? '...' : ''}
                          </div>
                        ) : '–'}
                      </td>
                      <td>
                        {visit.bathroomRating ? (
                          <span className="rating-display">🚻 {visit.bathroomRating}/10</span>
                        ) : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>📧 Send Email to All Users</DialogTitle>
            <button
              onClick={() => setShowEmailDialog(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X size={20} />
            </button>
          </DialogHeader>

          <div className="email-form">
            <div className="email-info">
              <p><strong>TO:</strong> support@dineasaurus.com</p>
              <p><strong>BCC:</strong> All users ({users.length} recipients)</p>
              <p className="email-note">Note: BCC recipients won't see each other's email addresses</p>
            </div>

            <div className="form-field">
              <label htmlFor="email-subject">Subject *</label>
              <input
                id="email-subject"
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
                className="email-input"
              />
            </div>

            <div className="form-field">
              <label htmlFor="email-message">Message *</label>
              <div className="email-controls">
                <label className="html-toggle">
                  <input
                    type="checkbox"
                    checked={enableHtml}
                    onChange={(e) => setEnableHtml(e.target.checked)}
                  />
                  <span>Enable HTML</span>
                </label>
                {enableHtml && (
                  <button
                    type="button"
                    onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                    className="preview-btn"
                  >
                    {showHtmlPreview ? '📝 Edit' : '👁️ Preview'}
                  </button>
                )}
              </div>
              {enableHtml && !showHtmlPreview && (
                <div className="html-toolbar">
                  <button type="button" onClick={() => insertHtmlTag('bold')} className="toolbar-btn" title="Bold">
                    <strong>B</strong>
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('italic')} className="toolbar-btn" title="Italic">
                    <em>I</em>
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('heading')} className="toolbar-btn" title="Heading">
                    H1
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('paragraph')} className="toolbar-btn" title="Paragraph">
                    ¶
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('link')} className="toolbar-btn" title="Link">
                    🔗
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('list')} className="toolbar-btn" title="Bullet List">
                    ≡
                  </button>
                </div>
              )}
              {!showHtmlPreview ? (
                <textarea
                  ref={emailTextareaRef}
                  id="email-message"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder={enableHtml ? "Enter HTML content (e.g., <h1>Title</h1><p>Text</p>)" : "Enter your message"}
                  rows={10}
                  className="email-textarea"
                />
              ) : (
                <div className="html-preview" dangerouslySetInnerHTML={{ __html: emailMessage }} />
              )}
              {enableHtml && (
                <small className="html-hint">
                  You can use HTML tags like &lt;h1&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;a href=""&gt;, etc.
                </small>
              )}
            </div>

            <div className="email-actions">
              <button
                onClick={() => setShowEmailDialog(false)}
                className="btn-cancel"
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                className="btn-send"
                disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
              >
                {sendingEmail ? 'Sending...' : '📧 Send Email'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Individual Email Dialog */}
      <Dialog open={showIndividualEmailDialog} onOpenChange={setShowIndividualEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>📧 Send Email to {selectedEmailUser?.display_name}</DialogTitle>
            <button
              onClick={() => setShowIndividualEmailDialog(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X size={20} />
            </button>
          </DialogHeader>

          <div className="email-form">
            <div className="email-info">
              <p><strong>TO:</strong> {selectedEmailUser?.email}</p>
              <p><strong>FROM:</strong> support@dineasaurus.com</p>
            </div>

            <div className="form-field">
              <label htmlFor="individual-subject">Subject *</label>
              <input
                id="individual-subject"
                type="text"
                value={individualSubject}
                onChange={(e) => setIndividualSubject(e.target.value)}
                placeholder="Enter email subject"
                className="email-input"
              />
            </div>

            <div className="form-field">
              <label htmlFor="individual-message">Message *</label>
              <div className="email-controls">
                <label className="html-toggle">
                  <input
                    type="checkbox"
                    checked={enableIndividualHtml}
                    onChange={(e) => setEnableIndividualHtml(e.target.checked)}
                  />
                  <span>Enable HTML</span>
                </label>
                {enableIndividualHtml && (
                  <button
                    type="button"
                    onClick={() => setShowIndividualHtmlPreview(!showIndividualHtmlPreview)}
                    className="preview-btn"
                  >
                    {showIndividualHtmlPreview ? '📝 Edit' : '👁️ Preview'}
                  </button>
                )}
              </div>
              {enableIndividualHtml && !showIndividualHtmlPreview && (
                <div className="html-toolbar">
                  <button type="button" onClick={() => insertHtmlTag('bold', true)} className="toolbar-btn" title="Bold">
                    <strong>B</strong>
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('italic', true)} className="toolbar-btn" title="Italic">
                    <em>I</em>
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('heading', true)} className="toolbar-btn" title="Heading">
                    H1
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('paragraph', true)} className="toolbar-btn" title="Paragraph">
                    ¶
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('link', true)} className="toolbar-btn" title="Link">
                    🔗
                  </button>
                  <button type="button" onClick={() => insertHtmlTag('list', true)} className="toolbar-btn" title="Bullet List">
                    ≡
                  </button>
                </div>
              )}
              {!showIndividualHtmlPreview ? (
                <textarea
                  ref={individualTextareaRef}
                  id="individual-message"
                  value={individualMessage}
                  onChange={(e) => setIndividualMessage(e.target.value)}
                  placeholder={enableIndividualHtml ? "Enter HTML content (e.g., <h1>Title</h1><p>Text</p>)" : "Enter your message"}
                  rows={10}
                  className="email-textarea"
                />
              ) : (
                <div className="html-preview" dangerouslySetInnerHTML={{ __html: individualMessage }} />
              )}
              {enableIndividualHtml && (
                <small className="html-hint">
                  You can use HTML tags like &lt;h1&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;a href=""&gt;, etc.
                </small>
              )}
            </div>

            <div className="email-actions">
              <button
                onClick={() => setShowIndividualEmailDialog(false)}
                className="btn-cancel"
                disabled={sendingEmail}
              >
                Cancel
              </button>
              <button
                onClick={handleSendIndividualEmail}
                className="btn-send"
                disabled={sendingEmail || !individualSubject.trim() || !individualMessage.trim()}
              >
                {sendingEmail ? 'Sending...' : '📧 Send Email'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>



    </div>
  );
}

export default AdminPanel;
